/**
 * DateRange Library - Single Source of Truth
 *
 * ARCHITEKTUR-REGELN (VERBINDLICH):
 *
 * 1) Half-Open Ranges: [start, end)
 *    - start <= timestamp < end
 *    - KEINE +1Tag/-1Sekunde Tricks
 *
 * 2) Zeitzone: Europe/Berlin
 *    - Alle Kalenderberechnungen in Berlin
 *    - DST-Wechsel werden korrekt behandelt
 *
 * 3) Begriffe:
 *    - DateRange: { start: Date, end: Date } - UTC Instants für DB-Queries
 *    - BusinessDate: YYYY-MM-DD String in Europe/Berlin
 *
 * Legacy Evidence:
 * - /opt/wash-ops/src/lib/dates.ts (getBerlinDayRange, computeBusinessRange)
 * - Schichtlogik: siehe shift-matcher.ts + ShiftRuleService (DB-basiert)
 */
const BERLIN_TZ = 'Europe/Berlin';
// =============================================================================
// BERLIN TIMEZONE HELPERS
// =============================================================================
/**
 * Berechnet den UTC-Offset für Berlin (inkl. DST)
 * @returns Offset in Millisekunden (Berlin - UTC)
 */
function getBerlinOffset(date) {
    const berlinStr = date.toLocaleString('en-US', { timeZone: BERLIN_TZ });
    const berlinDate = new Date(berlinStr);
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
    const utcDate = new Date(utcStr);
    return berlinDate.getTime() - utcDate.getTime();
}
/**
 * Formatiert ein Datum als YYYY-MM-DD in Berlin-Zeit
 */
export function formatBerlinDate(date) {
    return date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
}
/**
 * Gibt den aktuellen Tag in Berlin als YYYY-MM-DD zurück
 */
export function getTodayBerlin(now) {
    return formatBerlinDate(now || new Date());
}
/**
 * Gibt ein Datum N Tage in der Vergangenheit als YYYY-MM-DD zurück
 */
export function getDaysAgoBerlin(days, now) {
    const ref = now || new Date();
    const date = new Date(ref.getTime() - days * 24 * 60 * 60 * 1000);
    return formatBerlinDate(date);
}
// =============================================================================
// DAY RANGE (Half-Open [start, end))
// =============================================================================
/**
 * Gibt die Tagesgrenzen für ein Datum in Europe/Berlin zurück
 *
 * @param date - Referenzdatum (default: jetzt)
 * @returns { start, end } - start = 00:00 Berlin, end = 00:00 Berlin nächster Tag
 *
 * WICHTIG: Half-open [start, end) - Query: { gte: start, lt: end }
 *
 * Legacy Evidence: /opt/wash-ops/src/lib/dates.ts:49-71
 */
export function getBerlinDayRange(date) {
    const now = date || new Date();
    // Formatiere das Datum in Berlin-Zeit
    const berlinDateStr = now.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
    const [year, month, day] = berlinDateStr.split('-').map(Number);
    // KRITISCH: Offset für START und END separat berechnen (DST-Wechsel!)
    // Start: 00:00 Berlin dieses Tages
    const startRefUtc = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    const startOffset = getBerlinOffset(startRefUtc);
    const start = new Date(startRefUtc.getTime() - startOffset);
    // End: 00:00 Berlin nächster Tag (exklusiv)
    const endRefUtc = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0));
    const endOffset = getBerlinOffset(endRefUtc);
    const end = new Date(endRefUtc.getTime() - endOffset);
    return { start, end };
}
/**
 * Parst einen YYYY-MM-DD String und gibt die Tagesgrenzen in Berlin zurück
 */
export function parseBerlinDateRange(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Mittag um Timezone-Edge-Cases zu vermeiden
    const refDate = new Date(year, month - 1, day, 12, 0, 0);
    return getBerlinDayRange(refDate);
}
/**
 * "Gültig ab" (inklusives Kalenderdatum, YYYY-MM-DD) → gespeicherter
 * Zeitpunkt: 00:00:00.000 Berlin dieses Tages.
 *
 * Vorher wurde new Date('YYYY-MM-DD') = 00:00 UTC gespeichert — damit galt
 * die Regel erst ab 01:00/02:00 Berlin des angezeigten Tages (Review NW1).
 */
export function validityStartFromBerlinDate(dateStr) {
    return parseBerlinDateRange(dateStr).start;
}
/**
 * "Gültig bis" (inklusives Kalenderdatum, YYYY-MM-DD) → gespeicherter
 * Zeitpunkt: 23:59:59.999 Berlin dieses Tages.
 *
 * Der Matcher prüft exklusiv (timestamp > validUntil) — mit diesem Wert gilt
 * der letzte Tag vollständig. Bewusst NICHT 00:00 des Folgetags: So zeigt
 * formatBerlinDateISO/Anzeige weiterhin denselben Kalendertag und der
 * Formular-Roundtrip (laden → speichern) bleibt stabil — auch für
 * Alt-Bestände, die noch als 00:00 UTC gespeichert sind (Review NW1:
 * vorher war der letzte angezeigte Gültigkeitstag ab 01:00/02:00 Berlin
 * ausgeschlossen — die Samstagsregel-Falle vom 05.09.2026).
 */
export function validityEndFromBerlinDate(dateStr) {
    return new Date(parseBerlinDateRange(dateStr).end.getTime() - 1);
}
/**
 * Gibt die UTC-Grenzen für einen Datumsbereich zurück (Berlin)
 *
 * @param startStr - Start YYYY-MM-DD
 * @param endStr - End YYYY-MM-DD (inklusiv - wird zu exklusivem end+1 Tag)
 */
export function getDateRangeBounds(startStr, endStr) {
    const startRange = parseBerlinDateRange(startStr);
    const endRange = parseBerlinDateRange(endStr);
    return { start: startRange.start, end: endRange.end };
}
// =============================================================================
// ISO WEEK (Europe/Berlin)
// =============================================================================
/**
 * Berechnet die ISO-Kalenderwochennummer für ein Datum (Europe/Berlin)
 */
export function getISOWeekNumber(date) {
    const berlinDateStr = date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
    const [year, month, day] = berlinDateStr.split('-').map(Number);
    const d = new Date(Date.UTC(year, month - 1, day));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}
/**
 * Berechnet das ISO-Wochenjahr für ein Datum (Europe/Berlin)
 */
export function getISOWeekYear(date) {
    const berlinDateStr = date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
    const [year, month, day] = berlinDateStr.split('-').map(Number);
    const d = new Date(Date.UTC(year, month - 1, day));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
}
/**
 * Gibt Start (Mo) und Ende (So) einer ISO-Kalenderwoche als YYYY-MM-DD zurück
 */
export function getISOWeekBounds(year, week) {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4DayOfWeek = jan4.getUTCDay() || 7;
    const mondayKW1 = new Date(jan4);
    mondayKW1.setUTCDate(jan4.getUTCDate() - jan4DayOfWeek + 1);
    const monday = new Date(mondayKW1);
    monday.setUTCDate(mondayKW1.getUTCDate() + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return {
        start: formatBerlinDate(monday),
        end: formatBerlinDate(sunday),
    };
}
// =============================================================================
// MONTH (Europe/Berlin)
// =============================================================================
/**
 * Gibt den letzten Tag eines Monats als YYYY-MM-DD zurück
 * @param month - 1-indexed (1=Januar, 12=Dezember)
 */
export function getLastDayOfMonth(year, month) {
    const lastDay = new Date(year, month, 0);
    return formatBerlinDate(lastDay);
}
/**
 * Gibt den ersten Tag eines Monats als YYYY-MM-DD zurück
 */
export function getFirstDayOfMonth(year, month) {
    return `${year}-${String(month).padStart(2, '0')}-01`;
}
// =============================================================================
// QUICK PICKERS
// =============================================================================
const MONTH_NAMES_DE = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];
function formatDE(dateStr) {
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
}
/**
 * ZENTRALE QUICK PICKER FUNKTION
 *
 * @param picker - Quick Picker Typ
 * @param now - Referenzzeitpunkt (default: jetzt)
 * @returns BusinessDateRange mit Half-Open [start, end)
 *
 * Legacy Evidence: /opt/wash-ops/src/lib/dates.ts:294-431
 */
export function getQuickPickerRange(picker, now) {
    const refDate = now || new Date();
    const todayStr = formatBerlinDate(refDate);
    switch (picker) {
        case 'today': {
            const { start, end } = parseBerlinDateRange(todayStr);
            return {
                start,
                end,
                startDate: todayStr,
                endDate: todayStr,
                label: `Heute (${formatDE(todayStr)})`,
                isPartial: true,
            };
        }
        case 'yesterday': {
            const yesterdayStr = getDaysAgoBerlin(1, refDate);
            const { start, end } = parseBerlinDateRange(yesterdayStr);
            return {
                start,
                end,
                startDate: yesterdayStr,
                endDate: yesterdayStr,
                label: `Gestern (${formatDE(yesterdayStr)})`,
                isPartial: false,
            };
        }
        case 'dayBefore': {
            const dayBeforeStr = getDaysAgoBerlin(2, refDate);
            const { start, end } = parseBerlinDateRange(dayBeforeStr);
            return {
                start,
                end,
                startDate: dayBeforeStr,
                endDate: dayBeforeStr,
                label: `Vorgestern (${formatDE(dayBeforeStr)})`,
                isPartial: false,
            };
        }
        case 'thisWeek': {
            const year = getISOWeekYear(refDate);
            const week = getISOWeekNumber(refDate);
            const bounds = getISOWeekBounds(year, week);
            const { start } = parseBerlinDateRange(bounds.start);
            const { end } = parseBerlinDateRange(todayStr);
            return {
                start,
                end,
                startDate: bounds.start,
                endDate: todayStr,
                label: `KW ${week} (${formatDE(bounds.start)} - ${formatDE(todayStr)})`,
                isPartial: true,
            };
        }
        case 'lastWeek': {
            const lastWeekDate = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
            const year = getISOWeekYear(lastWeekDate);
            const week = getISOWeekNumber(lastWeekDate);
            const bounds = getISOWeekBounds(year, week);
            const { start, end } = getDateRangeBounds(bounds.start, bounds.end);
            return {
                start,
                end,
                startDate: bounds.start,
                endDate: bounds.end,
                label: `KW ${week} (${formatDE(bounds.start)} - ${formatDE(bounds.end)})`,
                isPartial: false,
            };
        }
        case 'thisMonth': {
            const berlinDateStr = refDate.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
            const [year, month] = berlinDateStr.split('-').map(Number);
            const firstDay = getFirstDayOfMonth(year, month);
            const { start } = parseBerlinDateRange(firstDay);
            const { end } = parseBerlinDateRange(todayStr);
            return {
                start,
                end,
                startDate: firstDay,
                endDate: todayStr,
                label: `${MONTH_NAMES_DE[month - 1]} ${year}`,
                isPartial: true,
            };
        }
        case 'lastMonth': {
            const berlinDateStr = refDate.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
            const [currentYear, currentMonth] = berlinDateStr.split('-').map(Number);
            const lastMonthDate = new Date(currentYear, currentMonth - 2, 1);
            const year = lastMonthDate.getFullYear();
            const month = lastMonthDate.getMonth() + 1;
            const firstDay = getFirstDayOfMonth(year, month);
            const lastDay = getLastDayOfMonth(year, month);
            const { start, end } = getDateRangeBounds(firstDay, lastDay);
            return {
                start,
                end,
                startDate: firstDay,
                endDate: lastDay,
                label: `${MONTH_NAMES_DE[month - 1]} ${year}`,
                isPartial: false,
            };
        }
    }
}
/**
 * Custom Range mit Validierung
 */
export function getCustomRange(startStr, endStr) {
    // Validierung: Start <= End
    const startDate = startStr <= endStr ? startStr : endStr;
    const endDate = startStr <= endStr ? endStr : startStr;
    const { start, end } = getDateRangeBounds(startDate, endDate);
    const isSingleDay = startDate === endDate;
    return {
        start,
        end,
        startDate,
        endDate,
        label: isSingleDay ? formatDE(startDate) : `${formatDE(startDate)} - ${formatDE(endDate)}`,
        isPartial: false,
    };
}
// =============================================================================
// DEBUG / INSPECTION
// =============================================================================
/**
 * Gibt alle Quick Picker Ranges als Debug-Info zurück
 */
export function getAllQuickPickerRanges(now) {
    const pickers = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'];
    const result = {};
    for (const picker of pickers) {
        result[picker] = getQuickPickerRange(picker, now);
    }
    return result;
}
/**
 * Formatiert eine DateRange für Debug-Ausgabe
 */
export function formatRangeForDebug(range) {
    return {
        startISO: range.start.toISOString(),
        endISO: range.end.toISOString(),
        startBerlin: range.start.toLocaleString('de-DE', { timeZone: BERLIN_TZ }),
        endBerlin: range.end.toLocaleString('de-DE', { timeZone: BERLIN_TZ }),
        durationHours: Math.round((range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60) * 100) / 100,
    };
}
//# sourceMappingURL=date-range.js.map